import { useLabelVersion } from '../../../../../../features/labels/LabelsProvider';
import { labelId, labelRegion } from '../../../../../../features/labels/labels';
import { useCallback, useEffect, useRef, useState } from "react";
import { useServer } from "../../../../../../context/ServerContext";
import { LogoutUsuario } from "../../../../../../utils/ApiLogin";
import { fetchConsolidadoCenters } from "../../../../services/nanooxService";
import { fetchConsolidadoService } from "../../../../../../utils/apiServer";
import FmdGoodIcon from "@mui/icons-material/FmdGood";
import PublicIcon from "@mui/icons-material/Public";
import TravelExploreIcon from "@mui/icons-material/TravelExplore";
import LogoutIcon from "@mui/icons-material/Logout";
import AppsIcon from "@mui/icons-material/Apps";
import SmartphoneIcon from "@mui/icons-material/Smartphone";
import EmailIcon from "@mui/icons-material/Email";
import LogoNanoox from "../../../../assets/img/APP_Nanoox_sin fondo.png";
import "./DrawerNanoox.css";

function formatName(value) {
  if (!value) return "";
  const parts = value.match(/[a-zA-Z0-9]+/g) || [value];
  return parts
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(" ");
}

function getRegionName(regionKey) {
  return formatName(regionKey);
}

function getAreaName(areaKey) {
  return formatName(areaKey.split("-")[1]);
}

function getCentroName(centroKey) {
  return formatName(centroKey.split("-")[0]);
}

function buildCentroKey({ centro = "", area = "", region = "" }) {
  if (!centro || !area || !region) {
    return null;
  }

  return `${centro}-${area}-${region}`;
}

function t(translate, key, fallback) {
  const value = typeof translate === "function" ? translate(key) : undefined;
  return value && value !== key ? value : fallback;
}

export default function DrawerNanoox({
  open,
  toggleDrawer,
  onSelectCenter,
  version,
  selectedCentro,
  setSelectedCentro,
  translate,
}) {
  useLabelVersion();
  const { servidor } = useServer();
  const [data, setData] = useState(null);
  const [serviciosCentro, setServiciosCentro] = useState({});
  const [selectedRegion, setSelectedRegion] = useState(null);
  const [selectedArea, setSelectedArea] = useState(null);
  const [, setLoadingCenter] = useState(false);
  const hasHydratedStoredSelection = useRef(false);

  useEffect(() => {
    const fetchData = async () => {
      const result = await fetchConsolidadoCenters("NANOOX", servidor);
      setData(result);
    };

    fetchData();
    const interval = setInterval(fetchData, 60000);
    return () => clearInterval(interval);
  }, [servidor]);

  useEffect(() => {
    const load = async () => setServiciosCentro(await fetchConsolidadoService(servidor));
    load();
    const interval = setInterval(load, 60000);
    return () => clearInterval(interval);
  }, [servidor]);

  function parseCentroKey(centroKey) {
    if (!centroKey) return {};
    const parts = centroKey.split("-");
    return {
      centro: parts[0] || "",
      area: parts[1] || "",
      region: parts[2] || "",
    };
  }

  const resolveSelectionFromCentroKey = useCallback((centroKey) => {
    const parsed = parseCentroKey(centroKey);
    const centro = parsed.centro || localStorage.getItem("CENTRO") || "";
    const areaSlug = parsed.area || localStorage.getItem("AREA") || "";
    const region = parsed.region || localStorage.getItem("REGION") || "";

    const areaKey =
      Object.keys(data?.areas || {}).find((key) => {
        const normalized = key.toLowerCase();
        return normalized.startsWith(`${region}`.toLowerCase()) && normalized.endsWith(`-${areaSlug}`.toLowerCase());
      }) || null;

    const centroKeyResolved =
      centro && areaSlug && region
        ? buildCentroKey({ centro, area: areaSlug, region })
        : null;

    return {
      region: region || null,
      area: areaKey,
      centro: centroKeyResolved,
    };
  }, [data]);

  useEffect(() => {
    if (!data) {
      return;
    }

    const fallbackCentroKey = selectedCentro
      ? selectedCentro
      : !hasHydratedStoredSelection.current
        ? buildCentroKey({
            centro: localStorage.getItem("CENTRO") || "",
            area: localStorage.getItem("AREA") || "",
            region: localStorage.getItem("REGION") || "",
          })
        : null;

    if (!fallbackCentroKey) {
      return;
    }

    hasHydratedStoredSelection.current = true;
    const nextSelection = resolveSelectionFromCentroKey(fallbackCentroKey);

    setSelectedRegion((current) => nextSelection.region || current);
    setSelectedArea((current) => nextSelection.area || current);

    if (nextSelection.centro && nextSelection.centro !== selectedCentro) {
      setSelectedCentro(nextSelection.centro);
    }
  }, [data, resolveSelectionFromCentroKey, selectedCentro, setSelectedCentro]);

  const clearStoredSelection = useCallback(() => {
    localStorage.removeItem("REGION");
    localStorage.removeItem("AREA");
    localStorage.removeItem("CENTRO");
  }, []);

  const handleLogout = useCallback(async () => {
    await LogoutUsuario(servidor);
    localStorage.clear();
    window.location.href = "/";
  }, [servidor]);

  const handleSelectCentro = async (centroKey, centerData = null) => {
    setLoadingCenter(true);
    setSelectedCentro(centroKey);

    const { centro, area, region } = parseCentroKey(centroKey);
    localStorage.setItem("REGION", region);
    localStorage.setItem("AREA", area);
    localStorage.setItem("CENTRO", centro);

    try {
      await onSelectCenter?.({
        region,
        area,
        centro,
        estado_centro: centerData?.estado_centro ?? centerData?.estado ?? null,
      });
    } finally {
      setLoadingCenter(false);
    }
  };

  const availableCenters = (() => {
    if (!selectedArea) return [];

    const areaKeyServicios = Object.keys(serviciosCentro).find(
      (key) => key.toLowerCase() === selectedArea.toLowerCase()
    );

    if (!areaKeyServicios) return [];

    const areaServicios = Object.fromEntries(
      Object.entries(serviciosCentro[areaKeyServicios] || {}).map(([key, value]) => [
        key.toLowerCase(),
        value,
      ])
    );

    return Object.entries(data?.centros?.[selectedArea] || {}).filter(([centroKey]) => {
      const centroServicios = areaServicios[centroKey.toLowerCase()];
      return centroServicios?.nanoox?.status_service === true;
    });
  })();

  return (
    <aside
      className={`drawer ${open ? "open" : ""}`}
      onMouseEnter={toggleDrawer(true)}
      onMouseLeave={toggleDrawer(false)}
    >
      <div className="drawer-content">
        <div className="drawer-section">
          <img
            src={LogoNanoox}
            alt="Nanoox"
            className="logo-nanoox"
            role="button"
            style={{ width: "6vw" }}
          />
        </div>

        <div style={{ display: "flex", justifyContent: "center" }}>
          <button
            className="drawer-btn special"
            onClick={() => {
              window.location = "/home";
            }}
          >
            <AppsIcon style={{ fontSize: "1vw" }} /> {t(translate, "home", "Home")}
          </button>
          <button
            className="drawer-btn special"
            onClick={handleLogout}
          >
            <LogoutIcon style={{ fontSize: "1vw" }} /> {t(translate, "cerrar_sesion", "Cerrar sesion")}
          </button>
        </div>

        <h2 className="drawer-title">{t(translate, "regions", "Regiones")}</h2>
        <div className="drawer-section">
          {Object.entries(data?.regiones || {}).map(([region]) => (
            <button
              key={region}
              className={`drawer-btn ${selectedRegion === region ? "active" : ""}`}
              onClick={() => {
                clearStoredSelection();
                setSelectedRegion(region);
                setSelectedArea(null);
                setSelectedCentro(null);
              }}
            >
              <PublicIcon style={{ fontSize: "1vw" }} /> {labelRegion(region, getRegionName(region))}
            </button>
          ))}
        </div>

        {selectedRegion && (
          <>
            <h2 className="drawer-title">{t(translate, "areas", "Areas")}</h2>
            <div className="drawer-section">
              {Object.entries(data?.areas || {})
                .filter(([area]) => area.startsWith(selectedRegion))
                .map(([area]) => (
                  <button
                    key={area}
                    className={`drawer-btn ${selectedArea === area ? "active" : ""}`}
                    onClick={() => {
                      clearStoredSelection();
                      setSelectedArea(area);
                      setSelectedCentro(null);
                    }}
                  >
                    <TravelExploreIcon style={{ fontSize: "1vw" }} /> {labelId(area, 'ra', getAreaName(area))}
                  </button>
                ))}
            </div>
          </>
        )}

        {selectedArea && (
          <>
            <h2 className="drawer-title">{t(translate, "centers", "Centros")}</h2>
            <div className="drawer-section">
              {availableCenters.map(([centroKey, centerData]) => (
                <button
                  key={centroKey}
                  className={`drawer-btn ${selectedCentro === centroKey ? "active" : ""}`}
                  onClick={() => handleSelectCentro(centroKey, centerData)}
                >
                  <FmdGoodIcon style={{ fontSize: "1vw" }} /> {labelId(centroKey, 'car', getCentroName(centroKey))}
                </button>
              ))}
            </div>
          </>
        )}
      </div>

      <footer className="drawer-footer">
        <p>
          <EmailIcon style={{ fontSize: "1vw", color: "#c3c3c3" }} /> {t(translate, "email_label", "Correo :")} informaciones@itgchile.com
        </p>
        <p>
          <SmartphoneIcon style={{ fontSize: "1vw", color: "#c3c3c3" }} /> {t(translate, "phone_label", "Fono :")} +56 (65) 22 52 881
        </p>
        <p>
          <SmartphoneIcon style={{ fontSize: "1vw", color: "#c3c3c3" }} /> {t(translate, "phone_label", "Fono :")} +56 (65) 22 50 080
        </p>
        <p>© ITG Chile v{version}</p>
      </footer>
    </aside>
  );
}
